<template>
  <v-content class="mt-0 pt-0">
  <v-container fluid>
    <pr-loading v-model="loading" />
    <v-snackbar v-model="msg.show" value="true" right top multi-line :color="msg.type" :timeout="msg.time" auto-height>
      <div v-html="msg.text"></div>
      <v-btn dark flat @click="msg.show = false">
        {{ $t('OK') }}
      </v-btn>
    </v-snackbar>

    <v-toolbar flat color="white" class="elevation-1">
      <v-toolbar-title>{{ $t('invoice screen') }}</v-toolbar-title>
      <v-divider
        class="mx-2"
        inset
        vertical
      ></v-divider>

      <v-btn-toggle v-model="dataFilter" @change="dateFilterChanged">
        <v-tooltip bottom v-for="(type,idx) in dataFilterTypes" :key="idx">
          <v-btn slot="activator" :value="type" flat>{{ type }}</v-btn>
          <span>{{ $t('Tooltip.dataFilter.' + type) }}</span>
        </v-tooltip>
        <v-tooltip bottom>
          <v-btn slot="activator" value="custom" flat>C</v-btn>
          <span>{{ $t('Tooltip.dataFilter.custom') }}</span>
        </v-tooltip>
      </v-btn-toggle>

      <template v-if="dataFilter === 'custom'">
        <v-input-date-picker v-model="dateSince" @input="dateFilterChanged" :label="$t('Date from')" hide-details clearable class="mx-2"></v-input-date-picker>
        <v-input-date-picker v-model="dateUntil" @input="dateFilterChanged" :label="$t('Date to'  )" hide-details clearable class="mx-2"></v-input-date-picker>
      </template>

      <v-spacer></v-spacer>
      <v-tooltip bottom>
        <v-btn icon slot="activator" @click="exportExcel" v-shortkey="['ctrl', 'alt', 'e']" @shortkey="exportExcel">
          <v-icon class="text--secondary">far fa-file-excel</v-icon>
        </v-btn>
        <span>{{ $t('Export to Excel') }}</span>
      </v-tooltip>
      <v-tooltip bottom>
        <v-btn icon slot="activator" @click="exportPdf" v-shortkey="['ctrl', 'alt', 'p']" @shortkey="exportPdf">
          <v-icon class="text--secondary">far fa-file-pdf</v-icon>
        </v-btn>
        <span>{{ $t('Export to PDF') }}</span>
      </v-tooltip>
      <v-tooltip bottom>
        <v-menu
          slot="activator"
          :close-on-content-click="false"
          :nudge-width="200"
          offset-x
        >
          <v-btn icon slot="activator">
            <v-icon class="text--secondary">settings</v-icon>
          </v-btn>

          <v-card>
            <v-list>
              <v-list-tile>
                <v-list-tile-content>
                  <v-list-tile-title>{{ $t('Table settings') }}</v-list-tile-title>
                  <v-list-tile-sub-title>{{ $t('Select visible columns') }}</v-list-tile-sub-title>
                </v-list-tile-content>
              </v-list-tile>
            </v-list>

            <v-divider></v-divider>

            <v-list>
              <v-list-tile v-for="(header,key) in headers" :key="key" v-if="header.value != 'actions'">
                <v-list-tile-action>
                  <v-switch v-model="header.show" color="purple" :disabled="columnHideCheck(header)" @change="storeFilter"></v-switch>
                </v-list-tile-action>
                <v-list-tile-title>{{ header.text }}</v-list-tile-title>
              </v-list-tile>
            </v-list>
          </v-card>
        </v-menu>
        <span>{{ $t('Table settings') }}</span>
      </v-tooltip>
    </v-toolbar>

    <v-layout row wrap>
      <v-flex xs12>
        <v-data-table
          :headers="headers"
          :items="data"
          :pagination.sync="pagination"
          :total-items="totalItems"
          :loading="loading"
          :rows-per-page-items="[5, 10, 25]"
          :rows-per-page-text="$t('Rows per page')"
          class="elevation-1"
        >
          <template slot="headers" slot-scope="props">
            <tr>
              <th
                v-for="header in props.headers"
                :key="header.text"
                v-if="header.show"
                :class="['column sortable', pagination.descending ? 'desc' : 'asc', header.value === pagination.sortBy ? 'active' : '']"
                @click="changeSort(header.value)"
              >
                <v-icon small>arrow_upward</v-icon>
                <v-tooltip bottom>
                  <span slot="activator">
                    {{ header.text }}
                  </span>
                  <span>
                    {{ header.text }}
                  </span>
                </v-tooltip>
              </th>
            </tr>
          </template>
          <template slot="items" slot-scope="props">
            <td v-for="(header,idx) in headers" :key="idx" v-if="header.show && header.value !== 'actions'" :class="getColumnAlign(idx)">{{ getObjectValue(props.item, idx) }}</td>
            <td class="justify-center align-center layout px-0">
              <v-tooltip bottom>
                <v-btn slot="activator" icon color="text--secondary" class="mx-0" @click="viewItem(props.item)">
                  <v-icon>pageview</v-icon>
                </v-btn>
                <span>{{ $t('Invoice details') }}</span>
              </v-tooltip>
              <v-tooltip bottom>
                <v-btn slot="activator" icon color="text--secondary" class="mx-0" v-if="!inProgress(props.item)" @click="printInvoicePdf(props.item)" v-shortkey="['ctrl', 'alt', 'i']" @shortkey="printInvoicePdf(props.item)">
                  <v-icon>print</v-icon>
                </v-btn>
                <v-progress-circular slot="activator" v-else indeterminate :size="24" :width="2" color="green"></v-progress-circular>
                <span>{{ $t('Print invoice PDF') }}</span>
              </v-tooltip>
              <v-tooltip bottom>
                <v-btn slot="activator" icon color="text--secondary" class="mx-0" @click="downloadAttachment(props.item)"
                  :color="props.item.attachmentLoading === undefined ? 'text--secondary' : 'grey--text text--lighten-1'"
                  :loading="props.item.attachmentLoading"
                >
                  <v-icon>attach_email</v-icon>
                </v-btn>
                <span>{{ $t('Download all attachments') }}</span>
              </v-tooltip>
            </td>
          </template>
        </v-data-table>
        <div class="text-xs-center pt-2">
          <v-pagination v-model="pagination.page" :length="pages" circle total-visible="7"></v-pagination>
        </div>
      </v-flex>
    </v-layout>
  </v-container>
  </v-content>
</template>

<script>
import _object from "lodash/object";
import { invoice, attachments } from "../resources";
import { extractBodyErrorMessages, downloadFile, msg, showDualAmount } from "../components/util";
import VInputDatePicker from "@/components/VInputDatePicker";
import PrLoading from "@/components/Loading";
import { mapGetters, mapActions } from "vuex";
import { setTimeout } from 'timers';

export default {
  components : {
    PrLoading,
    VInputDatePicker
  },
  data () {
    return {
      msg,

      loading: false,
      totalItems: 0,
      data: [],
      pagination: {},
      printing: [],

      dataFilter: '30',
      dataFilterTypes: [ '30', '60', '90' ],

      dateSince: new Date().setMonth(new Date().getMonth() - 5),
      dateUntil: new Date().valueOf(),

      userFuncs: {},

      headers: [
        { text: this.$t('Invoice Type'), value: 'type', type:'enum', enum: 'Invoice', align: 'center', sortable: false, show: true },
        { text: this.$t('Invoice Number'), value: 'number', align: 'right', sortable: false, show: true },
        { text: this.$t('Date'), value: 'date_invoiced', type: 'date', align: 'center', sortable: false, show: true },
        { text: this.$t('Due Date'), value: 'due_date', type: 'date',  align: 'center', sortable: false, show: true },
        { text: this.$t('Amount'), value: 'amount', display: 'showDualAmount', align: 'right', sortable: false, show: true },
        { text: this.$t('Paid'), value : 'paid',    align: 'center', sortable: false, show: true },
        { text: this.$t('Actions'), value: 'actions', align: 'center', sortable: false, show : true }
      ]
    }
  },

  computed : {
    ...mapGetters(['invoicesFilter']),

    pages () {
      if (this.pagination.rowsPerPage == null ||
        this.totalItems == null
      ) return 0;

      return Math.ceil(this.totalItems / this.pagination.rowsPerPage);
    }
  },

  watch: {
    pagination: {
      handler () {
        this.listData();
      },
      deep: true
    }
  },

  created () {
    this.$set(this.userFuncs, 'showDualAmount', showDualAmount);
  },

  mounted () {
    let storedFilter = null;
    try {
      storedFilter = JSON.parse(this.invoicesFilter);
    } catch(e) {
      storedFilter = null;
    }

    if (storedFilter) {
      this.dataFilter = storedFilter.dataFilter;
      this.dateSince = storedFilter.dateSince;
      this.dateUntil = storedFilter.dateUntil;
      if (storedFilter.pagination)
        this.pagination = storedFilter.pagination;
      for (let i = 0; i < storedFilter.shown.length; i++)
        this.headers[i].show = storedFilter.shown[i];

    } else {
      this.dateSince = new Date().setMonth(new Date().getMonth() - 12);
      this.dateUntil = new Date().valueOf();
    }

    this.getDataFromApi().then(data => {
        this.data = data.items;
        this.totalData = data.total;
      }, () => {
        console.log('mounting loading rejected');
      });
  },

  methods: {
    ...mapActions(['updateInvoicesFilter']),

    getObjectValue(obj, idx) {
      let v = _object.get(obj, this.headers[idx].value);
      let d = this.headers[idx].display;
      let t = this.headers[idx].type;
      if (d) {
        if (this.userFuncs[d])
          return this.userFuncs[d](v);
        else
          console.log("Display function " + d + " not found");
      }

      if (t === 'date')
        return v ? new Date(v).toLocaleDateString() : '';
      if (t === 'enum' && v) {
        let e = this.headers[idx].enum;
        if (!e)
          e = "Enum";
        return this.$t(e + '.' + v);
      }
      if (typeof v === 'boolean')
        return this.$t('Boolean.' + v);
      if (typeof v === 'number')
        return v.toLocaleString();
      return v;
    },

    getDataFromApi () {
      if (this.loading)
        return new Promise((resolve, reject) => reject());

      return new Promise((resolve, reject) => {
        const { sortBy, descending, page, rowsPerPage } = this.pagination;

        this.loading = true;
        let offset = (page>=1 && rowsPerPage>=1) ? (page - 1) * rowsPerPage : 0;
        let length = (rowsPerPage>=1) ? rowsPerPage : -1;
        let sort   = (sortBy) ? (descending ? "-" : "") + sortBy : "";
        let since = this.dateSince;
        let until = this.dateUntil;

        // check filtering
        let d = Number(this.dataFilter);
        if (isNaN(d)) {
          // custom date filter
        } else {
          until = new Date();
          since = new Date().setDate( until.getDate() - d );
          until = until.valueOf();
        }

        invoice.list({ since, until, length, offset, sort }).then(response => {
          const items = response.body.result;
          const total = response.body.length;
          resolve({
            items,
            total
          });
        }, response => {
          console.log("Error loading invoices " + response.status);
        }).finally(() => {
          this.loading = false;
        });
      });
    },

    listData() {
      this.storeFilter();
      this.getDataFromApi().then(data => {
        this.data = data.items;
        this.totalItems = data.total;
      }, () => {
          console.log('loading rejected');
      });
    },

    columnHideCheck(header) {
      if (!header.show)
        return false;

      let v = 0;
      for (let i = 0; i < this.headers.length; i++)
        if (this.headers[i].value != 'actions' && this.headers[i].show)
          v++;

      return (v<2);
    },

    getColumnAlign(idx) {
      if (this.headers[idx].align)
        return "text-xs-" + this.headers[idx].align;
      return "text-xs-left";
    },

    changeSort(column) {
      console.log("change sort of item #" + column);
      if (this.pagination.sortBy === column) {
        if (this.pagination.descending)
          this.pagination.sortBy = null;
        else
          this.pagination.descending = true;
      } else {
        this.pagination.sortBy = column;
        this.pagination.descending = false;
      }
    },

    dateFilterChanged() {
      if (!this.dataFilter)
       return;
      this.listData();
    },

    viewItem(item) {
      this.$router.push({ name : "invoice", params : { id : item.id }});
    },

    storeFilter() {
      let a = new Array();
      for (let i = 0; i < this.headers.length; i++)
        if (this.headers[i].value !== 'actions')
          a.push(this.headers[i].show);

      let s = {
        dateSince  : this.dateSince,
        dateUntil  : this.dateUntil,
        dataFilter : this.dataFilter,
        pagination : this.pagination,
        shown      : a
      };
      this.updateInvoicesFilter(JSON.stringify(s));
    },

    printInvoicePdf(item) {
      if (this.loading)
        return;

      let idx = this.printing.indexOf(item.id);
      if (idx < 0)
        this.loading = true;

      invoice.printPdf({ id : item.id }).then(response => {
        if (response.status === 200) {
          if (idx >= 0)
            this.printing.splice(idx, 1);
          downloadFile(response);
        }
        else if (response.status === 202) {
          this.msg.popup(this.$t('Invoice PDF print requested'), 'info');
          if (this.printing.indexOf(item.id) < 0)
            this.printing.push(item.id);
          setTimeout(() => this.printInvoicePdf({ id: item.id }), 5000);
        }
        else if (response.status === 204) {
          if (this.printing.indexOf(item.id) < 0)
            this.printing.push(item.id);
          setTimeout(() => this.printInvoicePdf({ id: item.id }), 5000);
        }
      }).catch(error => {
        this.msg.popup(error.statusText, 'error');
      }).finally(
        () => this.loading = false
      );
    },

    inProgress(item) {
      return this.printing.indexOf(item.id) >= 0;
    },

    exportPdf() {
      this.msg.popup(this.$t('Not implemented'), 'info');
    },

    exportExcel() {
      if (this.loading)
        return;

      const { sortBy, descending, page, rowsPerPage } = this.pagination;

      this.loading = true;
      let offset = (page>= 1) ? (page - 1) * rowsPerPage : 0;
      let length = (rowsPerPage>=1) ? rowsPerPage : -1;
      let since = this.dateSince;
      let until = this.dateUntil;

      // check filtering
      let d = Number(this.dataFilter);
      if (isNaN(d)) {
        // custom date filter
      } else {
        until = new Date();
        since = new Date().setDate( until.getDate() - d );
        until = until.valueOf();
      }
      invoice.xls({ length, offset, since, until }).then(response => {
        downloadFile(response);
      }, error => {
        this.msg.popup(error.statusText, 'error');
      }).finally(() => {
        this.loading = false;
      });
    },

    downloadAttachment(invoice) {
      if (invoice.attachmentLoading) {
        return;
      }
      this.$set(invoice, 'attachmentLoading', true);

      attachments.podLoadByInvoice({
        invoiceId: invoice.id
      }).then((response) => {

        const {status, link, filename} = response.body;

        if (status === 'IN_PROGRESS') {
          this.msg.popup(this.$t('ATTACHMENT.INVOICE.IN_PROGRESS', [filename]), 'info');
        }
        else
        if (status === 'DONE') {
          const linkText = '<a href="' + link + '" download>' + filename + '</a>';
          this.msg.popup(this.$t('ATTACHMENT.INVOICE.DONE', [linkText]), 'success', 0);
        }

      }).catch((err) => {
        console.log('Error downloading file', err);

        this.msg.popup( extractBodyErrorMessages(err.body) || this.$t('Error downloading file'), 'error' );

      }).finally(() => {
        this.$set(invoice, 'attachmentLoading', false);
      });
    },
  },

}
</script>

<style scoped>
</style>